import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { uploadService } from '../../../api/services/uploadService';
import { useAuth } from '../../../auth';
import { useOps } from '../../../hooks/useOps';

export interface UploadResult {
  succeeded: string[];
  failed: string[];
}

interface UseUploadMutationOptions {
  docType: string;
  onSuccess?: (result: UploadResult) => void;
  onError?: (error: Error) => void;
  onSettled?: () => void;
}

interface UseUploadMutationReturn {
  upload: (files: File[]) => void;
  isUploading: boolean;
  error: Error | null;
  reset: () => void;
}

export function useUploadMutation({
  docType,
  onSuccess,
  onError,
  onSettled,
}: UseUploadMutationOptions): UseUploadMutationReturn {
  const { user } = useAuth();
  const { getCurrentOpsConfig } = useOps();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (files: File[]): Promise<UploadResult> => {
      if (!user?.email) {
        throw new Error('Usuário não autenticado');
      }

      const opsConfig = getCurrentOpsConfig();
      const dbName = opsConfig?.dbName;

      if (!dbName) {
        throw new Error('Configuração de operação não encontrada');
      }

      return uploadService.uploadFiles(files, docType, user.email, dbName);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['file-status', docType] });
      onSuccess?.(result);
    },
    onError: (error: Error) => {
      onError?.(error);
    },
    onSettled: () => {
      onSettled?.();
    },
  });

  const upload = useCallback(
    (files: File[]) => {
      mutation.mutate(files);
    },
    [mutation]
  );

  const reset = useCallback(() => {
    mutation.reset();
  }, [mutation]);

  return {
    upload,
    isUploading: mutation.isPending,
    error: mutation.error as Error | null,
    reset,
  };
}
